// Page copy for the redesigned Studio. Keep enUS and zhCN structurally identical.
export const enUS = {
  title: 'API Keys',
  createKey: 'Create key',
  allApps: 'All apps',
  searchApps: 'Search apps',
  columns: {
    app: 'App name / ID',
    keys: 'API keys',
    created: 'Created',
    actions: 'Actions',
  },
  keyUnit: 'key | keys',
  appUnit: 'app | apps',
  footerNote: 'Keys are managed separately for each app',
  quietNote: 'Each app has its own ID and keys. Copy your initial key as soon as the app is created.',
  loading: 'Loading apps…',
  loadFailed: 'Could not load apps',
  retry: 'Retry',
  empty: {
    hint: 'Create an app to generate your first API key',
    noMatchTitle: 'No matching apps',
    noMatchHint: 'Try a different name or ID',
    clearSearch: 'Clear search',
  },
  contextStrip: 'Get credentials for your integration — your avatar selection is saved',
  backToIntegrate: 'Back to Integrate',
  backToApps: 'Back to API Keys',
  deleteApp: 'Delete app {name}',
  deleteAppDialog: {
    title: 'Delete app',
    intro: '“{name}” and all its API keys will be permanently deleted. Requests using these keys will stop working. This cannot be undone.',
    confirmLabel: 'Type “{name}” to confirm',
    submit: 'Delete app permanently',
    deleting: 'Deleting…',
    error: 'Could not delete this app. Please try again.',
  },
  createDialog: {
    title: 'Create API key',
    intro: 'We will generate your first API key when this app is created.',
    nameLabel: 'App name',
    namePlaceholder: 'e.g. Website experience',
    nameRequired: 'Enter an app name',
    submit: 'Create key',
    creating: 'Creating…',
  },
  keyFailedDialog: {
    title: 'App created, key pending',
    alertTitle: '{name} was created',
    alertBody: 'We could not generate the initial key. Your app is saved, so you do not need to create it again.',
    retry: 'Retry key generation',
  },
  createdDialog: {
    keyLabel: 'API key',
    viewApp: 'View app',
  },
  detail: {
    unavailableTitle: 'Content unavailable',
    createdOn: 'Created on {date}',
    appId: 'App ID',
    copyAppId: 'Copy app ID',
    keysTitle: 'API keys',
    noKeysTitle: 'No API keys yet',
    noKeysHint: 'Create a key to start integrating this app',
    generateInitial: 'Generate initial key',
    showKey: 'Show API key',
    hideKey: 'Hide API key',
    copyKey: 'Copy API key',
    deleteKey: 'Delete API key',
    loading: 'Loading app…',
    unavailableHeading: 'This item is unavailable',
    unavailableHint: 'It may have been removed or you may not have access.',
    openApps: 'Open API Keys',
    resumeTitle: 'Ready to integrate?',
    resumeContinueTitle: 'Continue your integration',
    resumeHint: 'Use this app with your selected avatar and connection guide',
    resumeAction: 'Use this app',
  },
  deleteDialog: {
    title: 'Delete API key',
    intro: 'Requests using this key will no longer be authenticated.',
    confirmLabel: 'Type the app name “{name}” to confirm',
    placeholder: 'Enter the full name',
    submit: 'Delete permanently',
  },
  sessionToken: {
    title: 'Temporary session token',
    active: 'Active',
    expired: 'Expired',
    generate: 'Generate token',
    regenerate: 'Generate new token',
    scope: 'Only Direct Mode clients need a session token. The recommended RTC integrations and Backend Mode never open the client-side connection this authenticates.',
    hint: 'For local Direct Mode testing. In production your own server mints tokens from the API key.',
    needKey: 'Create an API key for this app first',
    error: 'Could not generate a token. Try again.',
    expiresIn: 'Expires in',
    expiredHint: 'This token is no longer valid. Generate a new one to continue.',
    countdownLabel: 'Time until token expires',
    show: 'Show session token',
    hide: 'Hide session token',
    copy: 'Copy session token',
    footnote: '{hours}-hour validity · Cleared when you switch app or refresh',
  },
}

export const zhCN = {
  title: 'API Keys',
  createKey: '创建 Key',
  allApps: '全部应用',
  searchApps: '搜索应用',
  columns: {
    app: '应用名称 / ID',
    keys: 'API Key',
    created: '创建时间',
    actions: '操作',
  },
  keyUnit: '个 Key | 个 Key',
  appUnit: '个应用 | 个应用',
  footerNote: '每个应用的 Key 单独管理',
  quietNote: '每个应用都有独立的 ID 和 Key。应用创建后请立即复制初始 Key。',
  loading: '正在加载应用…',
  loadFailed: '无法加载应用',
  retry: '重试',
  empty: {
    hint: '创建应用即可生成第一个 API Key',
    noMatchTitle: '没有匹配的应用',
    noMatchHint: '换个名称或 ID 试试',
    clearSearch: '清除搜索',
  },
  contextStrip: '为你的接入获取凭证 — 已选的人物已保存',
  backToIntegrate: '返回 Integrate',
  backToApps: '返回 API Keys',
  deleteApp: '删除应用 {name}',
  deleteAppDialog: {
    title: '删除应用',
    intro: '“{name}”及其全部 API Key 将被永久删除，使用这些 Key 的请求将无法继续。此操作无法撤销。',
    confirmLabel: '输入“{name}”以确认',
    submit: '永久删除应用',
    deleting: '删除中…',
    error: '无法删除该应用，请重试。',
  },
  createDialog: {
    title: '创建 API Key',
    intro: '应用创建完成后，我们会同时生成第一个 API Key。',
    nameLabel: '应用名称',
    namePlaceholder: '例如：官网体验',
    nameRequired: '请输入应用名称',
    submit: '创建 Key',
    creating: '创建中…',
  },
  keyFailedDialog: {
    title: '应用已创建，Key 待生成',
    alertTitle: '{name} 已创建',
    alertBody: '初始 Key 生成失败。应用已保存，无需重复创建。',
    retry: '重试生成 Key',
  },
  createdDialog: {
    keyLabel: 'API Key',
    viewApp: '查看应用',
  },
  detail: {
    unavailableTitle: '内容不可用',
    createdOn: '创建于 {date}',
    appId: 'App ID',
    copyAppId: '复制 App ID',
    keysTitle: 'API Key',
    noKeysTitle: '还没有 API Key',
    noKeysHint: '创建一个 Key 开始接入这个应用',
    generateInitial: '生成初始 Key',
    showKey: '显示 API Key',
    hideKey: '隐藏 API Key',
    copyKey: '复制 API Key',
    deleteKey: '删除 API Key',
    loading: '正在加载应用…',
    unavailableHeading: '该内容不可用',
    unavailableHint: '它可能已被删除，或你没有访问权限。',
    openApps: '打开 API Keys',
    resumeTitle: '准备好接入了吗？',
    resumeContinueTitle: '继续你的接入',
    resumeHint: '把这个应用与已选人物和连接指南一起使用',
    resumeAction: '使用这个应用',
  },
  deleteDialog: {
    title: '删除 API Key',
    intro: '使用这个 Key 的请求将不再通过鉴权。',
    confirmLabel: '输入应用名称“{name}”以确认',
    placeholder: '输入完整名称',
    submit: '永久删除',
  },
  sessionToken: {
    title: '临时 Session Token',
    active: '有效',
    expired: '已过期',
    generate: '生成 Token',
    regenerate: '生成新 Token',
    scope: '只有 Direct Mode 客户端需要 Session Token。推荐的 RTC 接入方式和 Backend Mode 都不会打开这个 Token 所鉴权的客户端连接。',
    hint: '仅用于本地 Direct Mode 测试。生产环境中应由你自己的服务器用 API Key 签发 Token。',
    needKey: '请先为这个应用创建 API Key',
    error: '无法生成 Token，请重试。',
    expiresIn: '剩余有效期',
    expiredHint: '该 Token 已失效，请生成新的 Token。',
    countdownLabel: 'Token 剩余有效时间',
    show: '显示 Session Token',
    hide: '隐藏 Session Token',
    copy: '复制 Session Token',
    footnote: '有效期 {hours} 小时 · 切换应用或刷新后清除',
  },
}
